/* ========================================================= * Material Kit 2 PRO React - v2.0.0 ========================================================= * Product Page: https://www.creative-tim.com/product/material-kit-pro-react * Copyright 2021 Creative Tim (https://www.creative-tim.com) Coded by www.creative-tim.com ========================================================= * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. */

import { useState } from "react";
import { useMaterialUIController } from "/context";
import dynamic from "next/dynamic";
import Head from "next/head";
import bgImage from "/assets/img/bg-our-work.jpg";
import { findFeatures, findProjects, findTowns } from '/pages/api/our-work';
import config from "/config";
import MDBox from "/components/MDBox";

const Card = dynamic(() => import("@mui/material/Card"), { loading: () => null });
const Container = dynamic(() => import("@mui/material/Container"), { loading: () => null });
const Divider = dynamic(() => import("@mui/material/Divider"), { loading: () => null });
const Grid = dynamic(() => import("@mui/material/Grid"), { loading: () => null });
const Menu = dynamic(() => import("@mui/material/Menu"), { loading: () => null });
const MenuItem = dynamic(() => import("@mui/material/MenuItem"), { loading: () => null });
const Tooltip = dynamic(() => import("@mui/material/Tooltip"), { loading: () => null });
const CloseRounded = dynamic(() => import('@mui/icons-material/CloseRounded'), { loading: () => null });
const FilterListRounded = dynamic(() => import('@mui/icons-material/FilterListRounded'), { loading: () => null });
const KeyboardArrowDownRounded = dynamic(() => import('@mui/icons-material/KeyboardArrowDownRounded'), { loading: () => null });
const MDButton = dynamic(() => import('/components/MDButton'), { loading: () => null });
const PageLayout = dynamic(() => import("/examples/PageLayout"), { loading: () => null });
const HeroBanner = dynamic(() => import('/examples/HeroBanner'), { loading: () => null });
const GoogleAnalytics = dynamic(() => import('/examples/GoogleAnalytics'), { loading: () => null });
const ALLDCard = dynamic(() => import('/examples/ALLDCard'), { loading: () => null });
const browseOptions = ["Project", "Feature"];
const featuresFilter = ["All", "Outdoor Living", "Curb Appeal", "Whole Property"];

function OurWork({ featureCards, projectCards, towns }) {
  const [controller] = useMaterialUIController();
  // browse by filter
  const [browseBy, setBrowseBy] = useState(browseOptions[0]);
  const [anchorElBrowseBy, setAnchorElBrowseBy] = useState(null);
  // browse by project filter
  const [anchorElTowns, setAnchorElTowns] = useState(null);
  const [selectedTown, setSelectedTown] = useState("All");
  // browse by feature filter
  const [anchorElFeatures, setAnchorElFeatures] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState("All");

  return (
    <>
      <Head>
        <title key="title">Our Work | Autumn Leaf</title>
        <meta name="description" content="Check out what we design and build. We're Autumn Leaf, Long Island's Top Rated Landscape Design Company." />
        <link rel="canonical" href={`"${config.frontURL}/our-work"`} />
        <meta property="og:site_name" content="Autumn Leaf Landscape" />
        <meta property="og:url" content={`${config.frontURL}/our-work`} />
        <meta property="og:title" content="Work by Autumn Leaf" />
        <meta property="og:description" content="Check out what we design and build. We're Autumn Leaf, Long Island's Top Rated Landscape Design Company." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={`${config.frontURL}/images/social/our-work-facebook.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:title" content="Work by Autumn Leaf" />
        <meta name="twitter:description" content="Check out what we design and build. We're Autumn Leaf, Long Island's Top Rated Landscape Design Company." />
        <meta name="twitter:url" content={`${config.frontURL}/articles`} />
        <meta name="twitter:image" content={`${config.frontURL}/images/social/our-work-twitter.jpg`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                "@context": "https://schema.org/",
                "@type": "Blog",
                "@id": `${config.frontURL}/our-work`,
                "mainEntityOfPage": `${config.frontURL}/our-work`,
                "name": "Autumn Leaf Blog",
                "description": "Thoughts and tips about landscape design, landscape building, landscape maintenance and more",
                "publisher": {
                  "@type": "Organization",
                  "@id": config.frontURL,
                  "url": config.frontURL,
                  "name": "Autumn Leaf Landscape Design",
                  "logo": {
                    "@type": "ImageObject",
                    "@id": `${config.frontURL}/logo.png`,
                    "url": `${config.frontURL}/logo.png`,
                    "width": "180",
                    "height": "182"
                  }
                },
                "blogPost": [projectCards.map((row) =>
                (
                  {
                    "@type": "BlogPosting",
                    "@id": `${config.frontURL}/projects/${row.path}`,
                    "mainEntityOfPage": `${config.frontURL}/projects/${row.path}#main`,
                    "headline": row.title,
                    "name": row.title,
                    "description": row.description,
                    "datePublished": null,
                    "dateModified": null,
                    "author": {
                      "@type": "Organization",
                      "@id": config.frontURL,
                      "url": config.frontURL,
                      "name": "Autumn Leaf Landscape"
                    },
                    "image": {
                      "@type": "ImageObject",
                      "@id": `${config.frontURL}/${row.teaserUrl}`,
                      "url": `${config.frontURL}/${row.teaserUrl}`,
                      "height": `${row.teaserUrlHeight}`,
                      "width": `${row.teaserUrlWidth}`
                    },
                    "url": `${config.frontURL}/projects/${row.path}`,
                    "keywords": [row.category]
                  })
                )]
              })
          }}
        />
      </Head>
      <GoogleAnalytics />
      <PageLayout transparent light>
        <HeroBanner
          height="75vh"
          image={bgImage.src}
          imageAlt="3D Design we made for one of our design and build projects"
          prefix1="Our Work"
          prefix2=""
          typedJSContent={[""]}
          subtitle="Autumn Leaf Landscape Design"
          buttons={["phone"]}
        />
        <Card
          sx={{
            px: { xs: 0, lg: 2 },
            pt: 2,
            mx: { xs: 1, lg: 3 },
            mt: -8,
            mb: 4,
            boxShadow: ({ boxShadows: { xxl } }) => xxl,
          }}
        >
          <MDBox component="section" py={7}>
            <Container>
              <MDBox mt={-6} mb={6} display="flex">
                <MDBox sx={{ flexGrow: 1 }}>
                  <MDBox display="flex" sx={{ alignItems: "center" }}>
                    <Tooltip title="Browse by">
                      <span>
                        <MDButton
                          color={controller.darkMode ? "dark" : "white"}
                          onClick={(e) => setAnchorElBrowseBy(e.currentTarget)}
                          endIcon={<KeyboardArrowDownRounded />}
                          sx={{ whiteSpace: "nowrap" }}
                        >Browse by {browseBy}
                        </MDButton>
                      </span>
                    </Tooltip>
                    <MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
                {browseBy === "Project" && (
                  <MDButton
                    color={controller.darkMode ? "dark" : "white"}
                    onClick={(e) => {
                      if (selectedTown === "All") setAnchorElTowns(e.currentTarget)
                      else setSelectedTown("All")
                    }}
                    endIcon={
                      selectedTown === "All" ?
                        <FilterListRounded color="success" sx={{ marginTop: -0.1 }} /> :
                        <CloseRounded color="success" sx={{ marginTop: -0.1 }} />
                    }
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {selectedTown === "All" ? "Showing All" : selectedTown}
                  </MDButton>
                )}
                {browseBy === "Feature" && (
                  <>
                    <MDButton
                      color={controller.darkMode ? "dark" : "white"}
                      onClick={(e) => {
                        if (selectedFeature === "All") setAnchorElFeatures(e.currentTarget)
                        else setSelectedFeature("All")
                      }}
                      endIcon={
                        selectedFeature === "All" ?
                          <FilterListRounded color="success" sx={{ marginTop: -0.1 }} /> :
                          <CloseRounded color="success" sx={{ marginTop: -0.1 }} />
                      }
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {selectedFeature === "All" ? "Showing All" : selectedFeature}
                    </MDButton>
                  </>
                )}
              </MDBox>
              <Grid container spacing={3} id="main">
                {browseBy === "Project" && projectCards.length !== 0 && projectCards.filter((obj) => selectedTown === "All" ? obj : obj.town === selectedTown).map((row) => (
                  <ALLDCard key={row.id} row={row} pathPrefix="/our-work/project" type="Project" />
                ))}
                {browseBy === "Feature" && featureCards.length !== 0 && featureCards.filter((obj) => selectedFeature === "All" ? obj : obj.town === selectedFeature).map((row) => (
                  <ALLDCard key={row.id} row={row} pathPrefix="/our-work/feature" type="Feature" />
                ))}
              </Grid>
            </Container>
          </MDBox >
        </Card >
      </PageLayout>

      <Menu
        id="browse-by-menu"
        anchorEl={anchorElBrowseBy}
        open={Boolean(anchorElBrowseBy)}
        onClose={() => setAnchorElBrowseBy(null)}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
      >
        {browseOptions.map((row, index) => (
          <MenuItem key={`browseOption-${index}`} onClick={() => {
            setBrowseBy(row);
            setAnchorElBrowseBy(null);
            setSelectedFeature("All");
            setSelectedTown("All");
          }}>{row}</MenuItem>
        ))}
      </Menu>

      <Menu
        id="town-menu"
        anchorEl={anchorElTowns}
        open={Boolean(anchorElTowns)}
        onClose={() => setAnchorElTowns(null)}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        PaperProps={{ style: { maxHeight: 48 * 4.5, width: '22ch' } }}
      >
        {towns.map((row, index) => (
          <MDBox key={`menuTown-${index}`} >
            <MenuItem onClick={() => {
              setSelectedTown(row);
              setAnchorElTowns(null);
            }}>{row}</MenuItem>
            {row === "All" && (<Divider />)}
          </MDBox>
        ))}
      </Menu>

      <Menu
        id="feature-menu"
        anchorEl={anchorElFeatures}
        open={Boolean(anchorElFeatures)}
        onClose={() => setAnchorElFeatures(null)}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        PaperProps={{ style: { maxHeight: 48 * 4.5, width: '25ch' } }}
      >
        {featuresFilter.map((row, index) => (
          <MDBox key={`menuFeature-${index}`} >
            <MenuItem onClick={() => {
              setSelectedFeature(row);
              setAnchorElFeatures(null);
            }}>{row}</MenuItem>
            {row === "All" && (<Divider />)}
          </MDBox>
        ))}
      </Menu>
    </>
  );
}

export default OurWork;


export async function getStaticProps() {
  const projectCards = findProjects();
  const featureCards = findFeatures();
  const towns = findTowns();
  return { props: { featureCards, projectCards, towns } };
}
